import { MultiSelect, MultiSelectProps } from "@mantine/core"

interface DeferredMultiSelectProps extends Omit<MultiSelectProps, "value"> {
  value: string[]
  onCommit: (value: string[]) => void
  normalize?: (value: string[]) => string[]
  isEqual?: (left: string[], right: string[]) => boolean
  deferCommit?: boolean
}

function defaultIsEqual(left: string[], right: string[]) {
  if (left.length !== right.length) return false
  return left.every((value, index) => value === right[index])
}

export function DeferredMultiSelect({
  value,
  onCommit,
  normalize,
  isEqual = defaultIsEqual,
  deferCommit = true,
  onDropdownOpen,
  onDropdownClose,
  onChange,
  ...props
}: DeferredMultiSelectProps) {
  const [draft, setDraft] = React.useState<string[]>(value)
  const [opened, setOpened] = React.useState(false)
  const draftRef = React.useRef<string[]>(value)

  React.useEffect(() => {
    if (!isEqual(draftRef.current, value)) {
      draftRef.current = value
      setDraft(value)
    }
  }, [value, isEqual])

  const toStable = React.useCallback(
    (next: string[]) => (normalize ? normalize(next) : next),
    [normalize]
  )

  const commitIfChanged = React.useCallback(
    (next: string[]) => {
      const stable = toStable(next)
      if (!isEqual(value, stable)) {
        onCommit(stable)
      }
    },
    [isEqual, onCommit, toStable, value]
  )

  return (
    <MultiSelect
      {...props}
      value={draft}
      onDropdownOpen={() => {
        setOpened(true)
        onDropdownOpen?.()
      }}
      onDropdownClose={() => {
        setOpened(false)
        if (deferCommit) {
          commitIfChanged(draftRef.current)
        }
        onDropdownClose?.()
      }}
      onChange={(next) => {
        const stable = toStable(next)
        draftRef.current = stable
        setDraft(stable)

        if (!deferCommit || !opened) {
          commitIfChanged(stable)
        }

        onChange?.(stable)
      }}
    />
  )
}
